import { useQuery } from '@tanstack/react-query';
import { fetchGuestRegistrationStatus } from '../api/events.guest.api';
import { GUEST_STATUS_STALE_TIME_MS } from '../constants/guest-registration';
import { guestRegistrationKeys } from './events.keys';

/**
 * Whether a remembered token still identifies a live registration, with its
 * join details (FR-286). `GET /v1/events/:eventId/guest-registration` always
 * answers 200; `isRegistered: false` means the token is stale.
 *
 * @param eventId Target event.
 * @param token   The stored registration token, if any.
 * @param enabled Pass whether the viewer is logged out. A member's own status
 *   decides for them, and with no token there is nothing to ask, so the
 *   request is skipped in both cases.
 *
 * Never retried and bounded by a request timeout, so a slow or failing read
 * resolves quickly (the page then shows Register, keeping the stored entry).
 */
export function useGuestRegistrationStatus(eventId: string, token: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: guestRegistrationKeys.status(eventId, token ?? ''),
    queryFn: () => fetchGuestRegistrationStatus(eventId, token ?? ''),
    enabled: Boolean(eventId) && Boolean(token) && enabled,
    staleTime: GUEST_STATUS_STALE_TIME_MS,
    // A remembered token is checked before Register or Unregister is offered, so a failing read
    // must resolve at once to "not confirmed" instead of retrying behind the shared default.
    retry: false,
  });
}
